/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { jsx } from '@emotion/react'
import { type FunctionComponent } from 'react'

import { EuiButton, EuiButtonEmpty, EuiFlexGroup } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'

import useBillingNotificationsNav from '../../../hooks/useBillingNotificationsNav'

const submitButton = {
  create: {
    id: 'billing.notifications.budgets.budget-settings.button.create',
    defaultMessage: 'Create budget',
  },
  update: {
    id: 'billing.notifications.budgets.budget-settings.button.save',
    defaultMessage: 'Save budget',
  },
}

export type Props = {
  isEditingMode?: boolean
  isLoading: boolean | undefined
  isSubmitting: boolean
}

const ActionButtons: FunctionComponent<Props> = ({ isEditingMode, isLoading, isSubmitting }) => {
  const { goToBillingNotifications } = useBillingNotificationsNav()

  const handleCancelBudget = () => goToBillingNotifications()

  return (
    <EuiFlexGroup justifyContent='flexEnd' gutterSize='s'>
      <EuiButtonEmpty
        aria-label='Cancel'
        data-test-subj='billing.notifications.budgets.budget-settings.button.cancel'
        disabled={isSubmitting}
        onClick={handleCancelBudget}
        size='m'
      >
        <FormattedMessage
          id='billing.notifications.budgets.budget-settings.button.cancel'
          defaultMessage='Cancel'
        />
      </EuiButtonEmpty>
      <EuiButton
        color='primary'
        data-test-subj='billing.notifications.budgets.budget-settings.button.save'
        disabled={isLoading || isSubmitting}
        isLoading={isSubmitting}
        fill={true}
        type='submit'
        size='m'
      >
        <FormattedMessage {...submitButton[isEditingMode ? 'update' : 'create']} />
      </EuiButton>
    </EuiFlexGroup>
  )
}

export default ActionButtons
